import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import * as _ from 'lodash';
import { TrackerService } from '../../services/tracker.service';

/**
 * Sub-Tasks table for the Breakdown tab (#65237). Reuses <trackeritem-browse> for read display
 * and inline quick-edit when every child shares one tracker. When children span more than one
 * tracker, <trackeritem-browse> is not used at all -- its per-row status/priority/assignee
 * quick-edit and ACLs are resolved against a single component-level [tracker] input, which
 * would be wrong for a mixed set (per the implementation plan's decision not to generalize that
 * shared component's per-row tracker resolution in this iteration). Falls back to a small
 * read-only table linking each row to its own full edit page instead.
 */
@Component({
  selector: 'ti-breakdown-list',
  template: require('./trackeritem.breakdown-list.component.html')
})
export class BreakdownListComponent implements OnChanges {
  @Input() project: any;
  @Input() children: any[] = [];

  singleTracker: any = null;
  isLoadingTracker: boolean = false;

  constructor(private trackerService: TrackerService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.children) {
      this.loadSingleTracker();
    }
  }

  /**
   * child.tracker (embedded on each tracker item returned by rel=children) is only a bare
   * reference -- {id, trackerName, project}, no .fields -- since that's all the children-list
   * endpoint needs for its own read-only table. <trackeritem-browse> needs the *full* Tracker
   * (field config, workflow, etc.) to render its grid, which nothing here has ever fetched.
   * Load it explicitly and gate <trackeritem-browse> on that load completing, rather than
   * feeding it the bare reference and letting its template crash on missing field config.
   */
  private loadSingleTracker(): void {
    const trackerId = this.resolveSingleTrackerId();
    this.singleTracker = null;

    if (!trackerId) {
      return;
    }

    this.isLoadingTracker = true;
    this.trackerService.load(trackerId)
      .then((tracker: any) => {
        this.singleTracker = tracker;
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        this.isLoadingTracker = false;
      });
  }

  private resolveSingleTrackerId(): any {
    const trackerIds = [...new Set(_.map(this.children, (child) => child?.tracker?.id))];
    return trackerIds.length === 1 ? trackerIds[0] : null;
  }

  isClosed(child: any): boolean {
    return child?.statusId === 0;
  }

  statusLabel(child: any): string {
    return child?.extraFields?.status?.fieldData?.label || (this.isClosed(child) ? 'Closed' : 'Open');
  }
}
